/**
 * Rounding off a routed line for display.
 *
 * Mapbox returns route geometry simplified once for the whole route — around
 * 18 m between vertices on a typical road. That's invisible at normal zooms and
 * unmissable close in: at zoom 20 it's roughly 200 screen pixels between points,
 * so a sweeping bend renders as a series of straight lines with visible corners,
 * sitting on top of a road the map itself draws smoothly from tile data.
 *
 * A centripetal Catmull-Rom spline is the right tool. It *interpolates* — the
 * curve passes exactly through every original point — so the line cannot drift
 * off the road at the vertices, which a corner-cutting scheme like Chaikin
 * would do. The centripetal parameterisation matters too: the uniform version
 * overshoots and loops on sharp turns, which on a hairpin would draw the line
 * outside the bend entirely.
 *
 * For display only. Every measurement — distance along, off-route, rejoin — uses
 * the original points, so smoothing can never move where the app thinks you are.
 */

import { metersPerDegreeLng, METERS_PER_DEGREE_LAT } from './geo.js';
import type { LngLat } from './types.js';

export interface SmoothOptions {
  /**
   * Target spacing of the output, in metres. Segments shorter than this are
   * left alone, so a route that's already dense doesn't get needlessly bigger.
   */
  maxSpacingMeters?: number;
  /** Safety valve: a route long enough to blow past this is left as it is. */
  maxPoints?: number;
  /**
   * A turn of more than this many degrees at a single vertex is a corner, not a
   * bend, and is kept as one.
   */
  cornerDegrees?: number;
}

const DEFAULT_SPACING = 8;
const DEFAULT_MAX_POINTS = 20_000;

/**
 * Where a bend stops being a bend.
 *
 * A sweeping curve turns a couple of degrees per vertex; a junction turns
 * eighty in one. Smoothing through both made every sharp turn look soft — and
 * measurably wrong, because the map draws the road from the same points with an
 * actual angle at the corner, so the route line visibly cut across the road it
 * was meant to be on.
 *
 * Thirty degrees keeps hairpins and junctions crisp while leaving anything that
 * reads as a curve to be smoothed.
 */
const DEFAULT_CORNER_DEGREES = 30;

export function smoothPath(coords: LngLat[], options: SmoothOptions = {}): LngLat[] {
  const spacing = options.maxSpacingMeters ?? DEFAULT_SPACING;
  const maxPoints = options.maxPoints ?? DEFAULT_MAX_POINTS;

  if (coords.length < 3) return coords.slice();

  // Work in metres so the spline isn't distorted by longitude degrees being
  // shorter than latitude ones at this latitude.
  const refLat = coords[Math.floor(coords.length / 2)]![1];
  const mx = metersPerDegreeLng(refLat) || 1;
  const my = METERS_PER_DEGREE_LAT;
  const toM = (p: LngLat): [number, number] => [p[0] * mx, p[1] * my];
  const toLngLat = (p: [number, number]): LngLat => [p[0] / mx, p[1] / my];

  const pts = coords.map(toM);
  const corner = findCorners(pts, options.cornerDegrees ?? DEFAULT_CORNER_DEGREES);
  const out: Array<[number, number]> = [pts[0]!];

  for (let i = 0; i < pts.length - 1; i++) {
    /*
      Duplicating a control point is what gives a Catmull-Rom spline a corner:
      with no neighbour beyond it, the curve has no incoming tangent to honour
      and leaves the point straight down the segment. Used at the ends so the
      first and last segments still get a curve, and at every sharp turn so the
      line hinges there instead of rounding through it.
    */
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p0 = corner[i] ? p1 : (pts[i - 1] ?? p1);
    const p3 = corner[i + 1] ? p2 : (pts[i + 2] ?? p2);

    const length = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const steps = Math.max(1, Math.min(24, Math.ceil(length / spacing)));

    for (let step = 1; step <= steps; step++) {
      out.push(step === steps ? p2 : catmullRom(p0, p1, p2, p3, step / steps));
    }

    if (out.length > maxPoints) return coords.slice();
  }

  return out.map(toLngLat);
}

type Point = [number, number];

/** Which vertices turn sharply enough to be kept as corners. */
function findCorners(pts: Point[], cornerDegrees: number): boolean[] {
  // A turn of `cornerDegrees` away from straight on. Straight is cos 1, so a
  // sharper turn is a *smaller* cosine than the limit.
  const limit = Math.cos(cornerDegrees * (Math.PI / 180));
  const corner = new Array<boolean>(pts.length).fill(false);

  for (let i = 1; i < pts.length - 1; i++) {
    const ax = pts[i]![0] - pts[i - 1]![0];
    const ay = pts[i]![1] - pts[i - 1]![1];
    const bx = pts[i + 1]![0] - pts[i]![0];
    const by = pts[i + 1]![1] - pts[i]![1];

    const aLen = Math.hypot(ax, ay);
    const bLen = Math.hypot(bx, by);
    if (aLen === 0 || bLen === 0) continue;

    // cos of the angle between the two segments: 1 is dead straight on, -1 is
    // doubling back. Comparing cosines avoids an acos per vertex on a 10,000
    // point route.
    const cos = (ax * bx + ay * by) / (aLen * bLen);
    corner[i] = cos < limit;
  }

  return corner;
}

/**
 * Centripetal Catmull-Rom, evaluated at `t` between p1 and p2.
 *
 * The alpha of 0.5 is what makes it centripetal. At 0 (uniform) the curve
 * overshoots and can form a loop where three points turn sharply; at 1
 * (chordal) it pulls wide on long segments.
 */
function catmullRom(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const knot = (a: Point, b: Point, previous: number) =>
    previous + Math.pow(Math.hypot(b[0] - a[0], b[1] - a[1]), 0.5);

  const t0 = 0;
  const t1 = knot(p0, p1, t0);
  const t2 = knot(p1, p2, t1);
  const t3 = knot(p2, p3, t2);

  // Coincident points collapse the parameterisation; fall back to a straight
  // line rather than dividing by zero.
  if (t1 === t0 || t2 === t1 || t3 === t2) {
    return [p1[0] + (p2[0] - p1[0]) * t, p1[1] + (p2[1] - p1[1]) * t];
  }

  const time = t1 + (t2 - t1) * t;
  const a1 = lerp(p0, p1, (time - t0) / (t1 - t0));
  const a2 = lerp(p1, p2, (time - t1) / (t2 - t1));
  const a3 = lerp(p2, p3, (time - t2) / (t3 - t2));
  const b1 = lerp(a1, a2, (time - t0) / (t2 - t0));
  const b2 = lerp(a2, a3, (time - t1) / (t3 - t1));
  return lerp(b1, b2, (time - t1) / (t2 - t1));
}

function lerp(a: Point, b: Point, t: number): Point {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Thins a path down to roughly `maxPoints`, keeping its shape.
 *
 * For thumbnails and anything else that has to fit in a URL. Points are
 * dropped by arc length rather than by index, so a stretch with dense geometry
 * — a town, a hairpin — doesn't keep all of its vertices while a long straight
 * keeps none. The ends are always kept: a route that visibly stops short of
 * where it ends looks broken.
 *
 * Not Douglas-Peucker. That would preserve corners better for the same number
 * of points, but at thumbnail scale the difference is under a pixel, and this
 * is a single pass with no recursion.
 */
export function thinPath(coords: LngLat[], maxPoints = 80): LngLat[] {
  if (coords.length <= maxPoints || maxPoints < 2) return coords.slice();

  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    const [aLng, aLat] = coords[i - 1]!;
    const [bLng, bLat] = coords[i]!;
    const mLng = metersPerDegreeLng(aLat);
    cum.push(cum[i - 1]! + Math.hypot((bLng - aLng) * mLng, (bLat - aLat) * METERS_PER_DEGREE_LAT));
  }

  const total = cum[cum.length - 1]!;
  if (total <= 0) return [coords[0]!, coords[coords.length - 1]!];

  const out: LngLat[] = [coords[0]!];
  let cursor = 1;
  for (let step = 1; step < maxPoints - 1; step++) {
    const target = (total * step) / (maxPoints - 1);
    while (cursor < coords.length - 1 && cum[cursor]! < target) cursor++;
    const point = coords[cursor]!;
    const previous = out[out.length - 1]!;
    if (point[0] !== previous[0] || point[1] !== previous[1]) out.push(point);
  }
  out.push(coords[coords.length - 1]!);

  return out;
}
